import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  CalendarClock, 
  CheckCircle2, 
  AlertTriangle, 
  TrendingUp,
  Users,
  RefreshCw,
  ExternalLink
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import { formatCurrency } from '../../utils/formatUtils';

/**
 * SIP Dashboard Card - Shows SIP/Autopay metrics on admin dashboard
 * Displays: Active mandates, today's executions, open alerts, total invested
 */
export const SIPDashboardCard = ({ hasPermission, onNavigate }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAnalytics = async (showToast = false) => {
    try {
      if (showToast) setRefreshing(true);
      const response = await adminAPI.getSIPAnalytics();
      setAnalytics(response.data);
      if (showToast) toast.success('SIP data refreshed');
    } catch (error) {
      console.error('Failed to fetch SIP analytics:', error);
      // Don't show error toast on initial load - SIP might not be enabled
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  // If SIP is not enabled or no data, show minimal card
  if (!loading && !analytics) {
    return (
      <Card className="border-gold/20 mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-blue-600" />
            Gold SIP (Autopay)
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-sm text-muted-foreground">SIP feature not enabled or no data available.</p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="border-gold/20 mb-6 animate-pulse">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-blue-600" />
            Gold SIP (Autopay)
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const stats = [
    {
      icon: Users,
      iconClass: 'text-blue-600',
      value: analytics.active_mandates,
      label: 'Active SIPs',
      subValue: `${analytics.total_mandates} total`,
    },
    {
      icon: CheckCircle2,
      iconClass: 'text-green-600',
      value: analytics.executions_today,
      label: "Today's Debits",
      subValue: `${analytics.executions_this_month} this month`,
    },
    {
      icon: AlertTriangle,
      iconClass: analytics.alerts_open > 0 ? 'text-red-600' : 'text-gray-400',
      value: analytics.alerts_open,
      label: 'Open Alerts',
      subValue: analytics.alerts_open > 0 ? 'Needs review' : 'All clear',
      highlight: analytics.alerts_open > 0,
    },
    {
      icon: TrendingUp,
      iconClass: 'text-purple-600',
      value: formatCurrency(analytics.total_invested),
      label: 'Total Invested',
      subValue: `${analytics.total_milli_earned?.toFixed(2) || 0} Millis earned`,
    },
  ];

  return (
    <Card className="border-gold/20 mb-6">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-blue-600" />
            Gold SIP (Autopay)
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fetchAnalytics(true)}
              disabled={refreshing}
              className="h-7 px-2"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            {hasPermission('transactions') && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onNavigate('/admin/sip')}
                className="h-7 px-2 text-xs"
              >
                Manage <ExternalLink className="w-3 h-3 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div 
                key={index}
                className={`p-3 rounded-lg border ${
                  stat.highlight 
                    ? 'border-red-200 bg-red-50' 
                    : 'border-gray-100 bg-gray-50/50'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon className={`w-4 h-4 ${stat.iconClass}`} />
                  <span className="text-lg font-bold text-navy">{stat.value}</span>
                </div>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
                <p className={`text-xs mt-0.5 ${stat.highlight ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                  {stat.subValue}
                </p>
              </div>
            );
          })}
        </div>

        {/* Frequency Breakdown */}
        {analytics.mandates_by_frequency && Object.keys(analytics.mandates_by_frequency).length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs text-muted-foreground mb-2">By Frequency:</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(analytics.mandates_by_frequency).map(([freq, count]) => (
                <span 
                  key={freq}
                  className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded-full"
                >
                  {freq}: {count}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Failed executions warning */}
        {analytics.failed_executions_this_month > 0 && (
          <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-800">
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              {analytics.failed_executions_this_month} failed execution(s) this month
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SIPDashboardCard;
